import APIService from 'services/APIService'

const api = APIService()

const ALL_CONTRACTORS_PAGE_SIZE = 50

const listAllContractorPages = async (params) => {
  const first = await api.get('/me/contractors', { params: { ...params, page: 1, per_page: ALL_CONTRACTORS_PAGE_SIZE } })
  const lastPage = first?.data?.meta?.lastPage || 1
  if (lastPage <= 1) return first
  const rest = await Promise.all(
    Array.from({ length: lastPage - 1 }, (_, i) => api.get('/me/contractors', { params: { ...params, page: i + 2, per_page: ALL_CONTRACTORS_PAGE_SIZE } }))
  )
  const seen = new Set()
  const contractors = [first, ...rest]
    .flatMap(res => res?.data?.data?.contractors || [])
    .filter(c => (seen.has(c.id) ? false : seen.add(c.id)))
  return {
    ...first,
    data: {
      ...first.data,
      data: { ...first.data.data, contractors },
      meta: { ...first.data.meta, currentPage: lastPage }
    }
  }
}

export const listContractors = (params = {}) =>
  params.page == null
    ? listAllContractorPages(params)
    : api.get('/me/contractors', { params })

export const getContractorById = contractorId =>
  api.get('/me/contractors', { params: { 'filter[id]': contractorId, per_page: 1 } })
    .then(res => res?.data?.data?.contractors?.[0] || null)

export const listContractorActivity = contractorId =>
  api.get(`/me/contractors/${contractorId}/activity`)

export const createContractor = payload =>
  api.post('/me/contractors', payload)

export const extractContractorDetails = file => {
  const form = new FormData()
  form.append('file', file)
  return api.post('/me/contractors/extract-details', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const getContractorExtractStatus = token =>
  api.get(`/me/contractors/extract-details/${token}`)

export const updateContractor = (contractorId, payload) =>
  api.patch(`/me/contractors/${contractorId}`, payload)

export const listContractorApprovers = () =>
  api.get('/me/contractors/approvers')

export const bulkCreateContractors = contractors =>
  api.post('/me/contractors/bulk', { contractors })

export const getContractorMergeSuggestions = () =>
  api.get('/me/contractors/merge-suggestions')

export const mergeContractors = (survivorId, mergeIds) =>
  api.post('/me/contractors/merge', { survivorId, mergeIds })

export const undoContractorMerge = mergeId =>
  api.post(`/me/contractors/merges/${mergeId}/undo`)

export const extractContractorInsuranceValue = contractorId =>
  api.post(`/me/contractors/${contractorId}/extract-insurance-value`)

export const deleteContractor = contractorId =>
  api.delete(`/me/contractors/${contractorId}`)

export const listPurchaseOrders = contractorId =>
  api.get(`/me/contractors/${contractorId}/purchase-orders`)

export const listUnassignedPurchaseOrders = () =>
  api.get('/me/purchase-orders/unassigned')

export const createPurchaseOrder = (contractorId, payload) =>
  api.post(`/me/contractors/${contractorId}/purchase-orders`, payload)

export const updatePurchaseOrder = (purchaseOrderId, payload) =>
  api.patch(`/me/purchase-orders/${purchaseOrderId}`, payload)

export const deletePurchaseOrder = purchaseOrderId =>
  api.delete(`/me/purchase-orders/${purchaseOrderId}`)

export const addContractorDocument = (contractorId, payload) =>
  api.post(`/me/contractors/${contractorId}/documents`, payload)

export const deleteContractorDocument = (contractorId, documentId) =>
  api.delete(`/me/contractors/${contractorId}/documents/${documentId}`)

export const presignContractorDocument = (contractorId, payload) =>
  api.post(`/me/contractors/${contractorId}/documents/presign`, payload)

export const downloadContractorDocument = (contractorId, documentId) =>
  api.get(`/me/contractors/${contractorId}/documents/${documentId}/download`)

export const uploadContractorFile = async (contractorId, file) => {
  const presign = await presignContractorDocument(contractorId, {
    original_filename: file.name,
    mime_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
  })
  const { presignedUrl, s3Key } = presign.data.data
  const put = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  })
  if (!put.ok) throw new Error('Upload failed')
  return { s3Key, mimeType: file.type || 'application/octet-stream', sizeBytes: file.size }
}
